import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch, FiEdit3 } from "react-icons/fi";
import { formAPI } from "../../../../../services/api";
import "./11SubjectList.css";

const VIP11SubjectList = ({ projectId = "neuro-icu11" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subjects, setSubjects] = useState([]); // 전체 데이터 저장
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null); // 상세 에러 정보를 위한 state 추가
  const [searchInput, setSearchInput] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [userValue, setUserValue] = useState(null);
  const [currentHospital, setCurrentHospital] = useState(null);
  const [filteredSubjects, setFilteredSubjects] = useState([]); // 검색 필터링된 데이터
  const [paginatedSubjects, setPaginatedSubjects] = useState([]); // 페이지네이션된 데이터

  const ITEMS_PER_PAGE = 20;
  // 검색 필드 상수로 정의
  const SEARCH_FIELDS = ["hos_id", "data_id"]; // 11Read.js와 동일하게 변경

  // 마스터 계정 확인 (999 또는 225852484)
  const isMasterAccount = (userValue) => {
    return (
      userValue === 999 || userValue === 225852484 || userValue === "225852484"
    );
  };

  // user_value에 따른 병원 매핑
  const getUserHospital = (userValue) => {
    if (isMasterAccount(userValue)) return null; // 마스터 계정은 모든 병원 접근 가능

    const hospitalMap = {
      11: "A",
      12: "A", // 충남대
      24: "A", // 충남대 (추가)
      21: "B",
      22: "B", // 분당서울대
      31: "C",
      32: "C", // 영남대
      41: "D",
      42: "D", // 신촌세브란스
      51: "E",
      52: "E", // 서울대
      61: "F",
      62: "F", // 서울성모
      71: "G",
      72: "G", // G 병원
      81: "H",
      82: "H", // H 병원
      91: "I",
      92: "I", // I 병원
    };

    return hospitalMap[userValue] || null;
  };

  // 사용자 권한 확인 (홀수: 연구원, 짝수: 의사)
  const isDoctor = (userValue) => {
    if (isMasterAccount(userValue)) return true; // 마스터는 의사 권한
    return userValue % 2 === 0;
  };

  // URL 쿼리 파라미터 관리
  const currentParams = {
    search: searchParams.get("search") || "",
    page: parseInt(searchParams.get("page")) || 1,
  };

  // 쿼리 파라미터 업데이트 함수
  const updateQueryParams = (updates) => {
    const newParams = { ...currentParams, ...updates };
    setSearchParams(
      Object.entries(newParams)
        .filter(([_, value]) => value !== null && value !== "")
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
    );
  };

  // 검색어 입력 핸들러
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // 검색 버튼 클릭 핸들러
  const handleSearch = () => {
    updateQueryParams({ search: searchInput, page: 1 }); // 검색 시 첫 페이지로
  };

  // 검색 입력에서 Enter 키 처리
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
  };

  // 전체 데이터 조회 - userValue가 설정된 후 한 번만 실행
  useEffect(() => {
    // userValue가 아직 설정되지 않았으면 대기
    if (userValue === null) {
      console.log("[11SubjectList] userValue 대기 중...");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorDetails(null);

        console.log("[11SubjectList] 전체 데이터 조회 시작:", {
          projectId,
          userValue,
          userValueType: typeof userValue,
          isMaster: userValue === 999,
        });

        // VIP11 백엔드 API를 사용한 전체 데이터 조회 (서버 사이드 필터링)
        const vip11ApiUrl = "https://product.idea2m.com/api/vip11/get-data"; // 하드코딩
        const response = await fetch(vip11ApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            search: "", // 검색어 없이 전체 데이터 조회
            searchFields: SEARCH_FIELDS,
            userValue: userValue, // 서버 사이드 필터링을 위한 userValue 전송
          }),
        });

        if (!response.ok) {
          throw new Error(
            `API 호출 실패: ${response.status} ${response.statusText}`,
          );
        }

        const responseData = await response.json();

        if (responseData.success) {
          setSubjects(responseData.rows); // 전체 데이터 저장
          setTotalCount(responseData.totalCount);

          console.log("[11SubjectList] 전체 데이터 로드 완료:", {
            totalRows: responseData.rows.length,
            userValue: responseData.userValue,
            filteredByUserValue: responseData.filteredByUserValue,
            allUniqueUserIds: [
              ...new Set(responseData.rows.map((row) => row.user_id)),
            ],
          });
        } else {
          throw new Error(responseData.message || "데이터 조회 실패");
        }
      } catch (err) {
        console.error("[11SubjectList] 데이터 조회 중 오류:", err);
        setErrorDetails({
          step: err.response?.data?.step,
          message: err.response?.data?.message,
          error: err.response?.data?.error,
          sql: err.response?.data?.error?.sql,
          sqlMessage: err.response?.data?.error?.sqlMessage,
        });
        setError(err.message || "데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, userValue]); // 검색어와 페이지 의존성 제거

  // 클라이언트 사이드 검색 필터링
  useEffect(() => {
    let filtered = subjects;

    // 검색어가 있는 경우 필터링
    if (currentParams.search && currentParams.search.trim()) {
      const searchTerm = currentParams.search.toLowerCase().trim();
      filtered = subjects.filter((subject) => {
        return SEARCH_FIELDS.some((field) => {
          const value = subject.form_data?.[field] || subject[field];
          return value && value.toString().toLowerCase().includes(searchTerm);
        });
      });
    }

    setFilteredSubjects(filtered);

    console.log("[11SubjectList] 클라이언트 필터링 완료:", {
      totalSubjects: subjects.length,
      filteredSubjects: filtered.length,
      searchTerm: currentParams.search,
    });
  }, [subjects, currentParams.search]);

  // 클라이언트 사이드 페이지네이션
  useEffect(() => {
    const startIndex = (currentParams.page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginated = filteredSubjects.slice(startIndex, endIndex);

    setPaginatedSubjects(paginated);

    console.log("[11SubjectList] 페이지네이션 적용:", {
      currentPage: currentParams.page,
      startIndex,
      endIndex,
      paginatedCount: paginated.length,
      totalPages: Math.ceil(filteredSubjects.length / ITEMS_PER_PAGE),
    });
  }, [filteredSubjects, currentParams.page]);

  // user_value 조회
  useEffect(() => {
    if (!projectId) return;

    (async () => {
      try {
        const res = await formAPI.getUserValue(projectId);
        const val = res.userValue ?? res.user_value ?? null;
        setUserValue(val);

        // 현재 사용자의 병원 설정
        const hospital = getUserHospital(val);
        setCurrentHospital(hospital);

        console.log("현재 사용자 정보:", {
          userValue: val,
          hospital: hospital,
          isDoctor: isDoctor(val),
          isMaster: isMasterAccount(val),
        });
      } catch (err) {
        console.error("사용자 정보 조회 실패:", err);
      }
    })();
  }, [projectId]);

  // 개선된 페이지네이션 컴포넌트
  const Pagination = () => {
    const filteredTotalCount = filteredSubjects.length;
    const totalPages = Math.ceil(filteredTotalCount / ITEMS_PER_PAGE);
    const currentPage = currentParams.page;

    // 표시할 페이지 번호 범위 계산 (최대 10개 버튼, 화면 크기에 따라 조정)
    const maxPageButtons = Math.min(10, totalPages);
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // 시작 페이지 조정
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    // 화면이 작은 경우 더 적은 버튼 표시
    const actualButtons = endPage - startPage + 1;

    // 10페이지 단위 이동 계산
    const currentGroup = Math.ceil(currentPage / 10);
    const totalGroups = Math.ceil(totalPages / 10);
    const prevGroupStart = Math.max(1, (currentGroup - 2) * 10 + 1);
    const nextGroupStart = Math.min(totalPages, currentGroup * 10 + 1);

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          전체 {filteredTotalCount}건 / {totalPages} 페이지 중 {currentPage}{" "}
          페이지
        </div>
        <div className="pagination">
          {/* 10페이지 단위 이동 버튼들 */}
          <div className="pagination-group-navigation">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="pagination-button group-nav"
              title="첫 페이지"
            >
              ≪
            </button>
            <button
              onClick={() => handlePageChange(prevGroupStart)}
              disabled={currentGroup === 1}
              className="pagination-button group-nav"
              title="이전 10페이지"
            >
              ‹
            </button>
          </div>

          {/* 페이지 번호 버튼들 */}
          <div className="pagination-numbers">
            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i,
            ).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`pagination-button ${
                  pageNum === currentPage ? "active" : ""
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          {/* 10페이지 단위 이동 버튼들 */}
          <div className="pagination-group-navigation">
            <button
              onClick={() => handlePageChange(nextGroupStart)}
              disabled={currentGroup === totalGroups}
              className="pagination-button group-nav"
              title="다음 10페이지"
            >
              ›
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-button group-nav"
              title="마지막 페이지"
            >
              ≫
            </button>
          </div>
        </div>

        {/* 기존 네비게이션 (단일 페이지 이동) */}
        <div className="pagination-single-navigation">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button single-nav"
          >
            이전
          </button>
          <span className="current-page-indicator">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="pagination-button single-nav"
          >
            다음
          </button>
        </div>
      </div>
    );
  };

  const handleEditSubject = (subject) => {
    console.log("[VIP11SubjectList] 편집 요청:", {
      dataId: subject.data_id,
      userValue,
      isMaster: userValue === 999,
      serverFiltered: true, // 서버에서 이미 필터링된 데이터이므로 편집 가능
    });

    // 서버에서 이미 해당 사용자가 접근 가능한 데이터만 전송했으므로
    // 추가적인 권한 체크 없이 편집 허용
    navigate(`/ecrf/subjectlist/edit/${subject.data_id}`, {
      state: {
        projectId: "11",
        formData: subject.form_data,
      },
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Complete: { color: "#10b981", bg: "#d1fae5" },
      "In process": { color: "#f59e0b", bg: "#fef3c7" },
      "Not started": { color: "#ef4444", bg: "#fee2e2" },
    };

    const config = statusConfig[status] || statusConfig["Not started"];

    return (
      <span
        className="status-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {status || "Not started"}
      </span>
    );
  };

  const renderCellContent = (subject, column) => {
    const value = subject.form_data[column.id] || subject[column.id];

    switch (column.type) {
      case "status":
        return getStatusBadge(value);
      case "date":
        return value ? new Date(value).toLocaleDateString() : "-";
      default:
        return value || "-";
    }
  };

  // 고정 컬럼 정의
  const columns = [
    {
      id: "__edit__",
      label: "Edit",
      type: "edit",
      sortable: false,
      width: "80px",
    },
    {
      id: "hos_id",
      label: "HOS_ID",
      type: "text",
      sortable: true,
      width: "120px",
    },
    {
      id: "data_id",
      label: "DATA_ID",
      type: "text",
      sortable: true,
      width: "120px",
    },
    {
      id: "initial_status",
      label: "Initial",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "icu_status",
      label: "ICU",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "daily_status",
      label: "Daily",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "additional_daily_status",
      label: "Additional Daily",
      type: "status",
      sortable: true,
      width: "120px",
    },
    {
      id: "early_outcome_status",
      label: "Early Outcome",
      type: "status",
      sortable: true,
      width: "120px",
    },
    {
      id: "hosp_discharge_status",
      label: "Hospital Discharge",
      type: "status",
      sortable: true,
      width: "140px",
    },
    {
      id: "surgeries_status",
      label: "Surgeries",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "Final_status",
      label: "Final Outcome",
      type: "status",
      sortable: true,
      width: "120px",
    },
  ];

  if (loading) {
    return (
      <div className="vip-subject-list-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>환자 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vip-subject-list-container">
        <div className="error-message">
          <h3>오류가 발생했습니다</h3>
          <p>{error}</p>
          {errorDetails && (
            <div className="error-details">
              <h4>상세 오류 정보:</h4>
              <ul>
                {errorDetails.step && <li>발생 단계: {errorDetails.step}</li>}
                {errorDetails.message && (
                  <li>오류 메시지: {errorDetails.message}</li>
                )}
                {errorDetails.sqlMessage && (
                  <li>SQL 오류: {errorDetails.sqlMessage}</li>
                )}
                {errorDetails.sql && <li>실행 쿼리: {errorDetails.sql}</li>}
                {errorDetails.error &&
                  typeof errorDetails.error === "object" && (
                    <li>
                      추가 정보:
                      <pre>{JSON.stringify(errorDetails.error, null, 2)}</pre>
                    </li>
                  )}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="vip-subject-list-container"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      {
        <>
          <div className="filter-section">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder={`${SEARCH_FIELDS.join(", ")} 검색...`} // 검색 필드를 placeholder에 표시
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <button
                onClick={handleSearch}
                className="search-button"
                title="검색"
              >
                <FiSearch className="search-button-icon" />
              </button>
            </div>
          </div>

          <div className="table-container">
            <table className="subject-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.id}
                      className={column.sortable ? "sortable" : ""}
                      style={{ width: column.width }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedSubjects.map((subject) => (
                  <tr key={subject.id}>
                    {columns.map((column) => (
                      <td key={column.id}>
                        {column.id === "__edit__" ? (
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEditSubject(subject)}
                            title="Edit"
                          >
                            <FiEdit3 />
                          </button>
                        ) : (
                          renderCellContent(subject, column)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination />

          {paginatedSubjects.length === 0 && !loading && (
            <div className="empty-state">
              <p>
                {filteredSubjects.length === 0
                  ? "조건에 맞는 환자가 없습니다."
                  : "현재 페이지에 표시할 데이터가 없습니다."}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  backgroundColor: "#1976D2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
                </svg>
                새로고침
              </button>
            </div>
          )}
        </>
      }
    </div>
  );
};

export default VIP11SubjectList;
